// src/services/GuestAuthService.js - NEW FILE
import { 
  signInAnonymously, 
  updateProfile,
  linkWithCredential,
  GoogleAuthProvider
} from 'firebase/auth';
import { safeSignInWithPopup } from '../firebase/config';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

export const GuestAuthService = {
  /**
   * Create anonymous account for guest
   * @param {Object} guestInfo - { firstName, phoneNumber, displayName }
   */
  async createGuestAccount(guestInfo) {
  try {
    console.log('🎭 Creating guest account for phone:', guestInfo.phoneNumber);
    
    // ✅ STEP 1: Check if account already exists
    const existingAccount = await this.findGuestByPhone(guestInfo.phoneNumber);
    
    if (existingAccount) {
      console.log('⚠️ Guest account already exists, returning existing:', existingAccount.uid);
      
      // Sign in anonymously (creates Firebase session)
      await signInAnonymously(auth);
      
      return {
        uid: existingAccount.uid,
        displayName: existingAccount.displayName,
        phoneNumber: existingAccount.phoneNumber,
        isGuest: true,
        isExisting: true  // ✅ Flag to indicate this is existing account
      };
    }
    
    console.log('🆕 No existing account, creating new anonymous account');
    
    // ✅ STEP 2: Create NEW anonymous account
    const userCredential = await signInAnonymously(auth);
    const user = userCredential.user;
    
    console.log('✅ Anonymous user created:', user.uid);
    
    // Update display name
    await updateProfile(user, {
      displayName: guestInfo.displayName || guestInfo.firstName
    });
    
    // ✅ STEP 3: Store guest metadata in Firestore with PHONE as key field
    await setDoc(doc(db, 'users', user.uid), {
      isGuest: true,
      isAnonymous: true,
      phoneNumber: guestInfo.phoneNumber,  // ✅ CRITICAL: Index for lookup
      guestInfo: {
        firstName: guestInfo.firstName,
        phoneNumber: guestInfo.phoneNumber,
        displayName: guestInfo.displayName,
        createdAt: new Date().toISOString()
      },
      createdAt: new Date().toISOString()
    });
    
    // ✅ STEP 4: Store in localStorage
    localStorage.setItem('guest_session', JSON.stringify({
      guestId: user.uid,
      phoneNumber: guestInfo.phoneNumber,
      displayName: guestInfo.displayName,
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    }));
    
    console.log('✅ Guest account created and stored');
    
    return {
      uid: user.uid,
      displayName: guestInfo.displayName,
      phoneNumber: guestInfo.phoneNumber,
      isGuest: true,
      isExisting: false
    };
    
  } catch (error) {
    console.error('❌ Error creating guest account:', error);
    throw error;
  }
},
  
  /**
   * Find existing guest account by phone number
   */
  async findGuestByPhone(phoneNumber) {
    try {
      console.log('🔍 Searching for guest account with phone:', phoneNumber);
      
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      
      const usersRef = collection(db, 'users');
      const q = query(
        usersRef, 
        where('phoneNumber', '==', phoneNumber),
        where('isGuest', '==', true)
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        console.log('❌ No guest account found');
        return null;
      }
      
      const userDoc = snapshot.docs[0];
      const userData = userDoc.data();
      
      console.log('✅ Found guest account:', userDoc.id);
      
      return {
        uid: userDoc.id,
        ...userData.guestInfo,
        isGuest: true
      };
      
    } catch (error) {
      console.error('❌ Error finding guest:', error);
      return null;
    }
  },
  
  /**
   * Sign in existing guest by phone lookup
   */
  async signInGuest(phoneNumber) {
    try {
      console.log('🔐 Attempting guest sign-in for:', phoneNumber);
      
      // Check localStorage first
      const storedGuest = localStorage.getItem('guest_account');
      if (storedGuest) {
        const guestData = JSON.parse(storedGuest);
        if (guestData.phoneNumber === phoneNumber) {
          console.log('✅ Found guest in localStorage, checking if still valid...');
          
          const expiresAt = new Date(guestData.expiresAt);
          if (expiresAt > new Date()) {
            // Try to use stored UID
            const userDoc = await getDoc(doc(db, 'users', guestData.uid));
            if (userDoc.exists() && userDoc.data().isGuest) {
              console.log('✅ Guest account still valid, returning data');
              return guestData;
            }
          }
        }
      }
      
      // Lookup in Firestore
      const guestAccount = await this.findGuestByPhone(phoneNumber);
      
      if (!guestAccount) {
        throw new Error('No guest account found with this phone number');
      }
      
      // Store in localStorage
      localStorage.setItem('guest_account', JSON.stringify({
        uid: guestAccount.uid,
        phoneNumber: guestAccount.phoneNumber,
        displayName: guestAccount.displayName,
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
      }));
      
      return guestAccount;
      
    } catch (error) {
      console.error('❌ Error signing in guest:', error);
      throw error;
    }
  },
  
  /**
   * Convert anonymous account to permanent (link with Google)
   */
  async convertToPermanent() {
    try {
      console.log('🔄 Converting anonymous account to permanent');
      
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.isAnonymous) {
        throw new Error('No anonymous user to convert');
      }
      
      // Sign in with Google
      const provider = new GoogleAuthProvider();
  const result = await safeSignInWithPopup(provider);
  const credential = GoogleAuthProvider.credentialFromResult(result);
      
      // Link accounts
      const linkedUser = await linkWithCredential(currentUser, credential);
      
      console.log('✅ Account linked:', linkedUser.user.uid);
      
      // Update user document
      await setDoc(doc(db, 'users', linkedUser.user.uid), {
        isGuest: false,
        isAnonymous: false,
        upgradedAt: new Date().toISOString(),
        email: linkedUser.user.email,
        displayName: linkedUser.user.displayName,
        photoURL: linkedUser.user.photoURL
      }, { merge: true });
      
      // Clear guest localStorage
      localStorage.removeItem('guest_account');
      
      return linkedUser.user;
      
    } catch (error) {
      console.error('❌ Error converting account:', error);
      throw error;
    }
  },
  
  /**
   * Check if current user is a guest
   */
  isCurrentUserGuest() {
    const user = auth.currentUser;
    return user?.isAnonymous === true;
  },
  
  /**
   * Get guest metadata for current user
   */
  async getCurrentGuestInfo() {
    const user = auth.currentUser;
    if (!user?.isAnonymous) return null;
    
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) return null;
    
    const userData = userDoc.data();
    return userData.isGuest ? userData.guestInfo : null;
  }
};